import { useState, useEffect, useCallback } from 'react';
import { feedbackService } from '../../../../services/feedback/feedbackService';
import type {
  FeedbackMetrics,
  FeedbackListResponse,
} from '../../../../types/feedback';

interface UseFeedbackAnalyticsResult {
  metrics: FeedbackMetrics | null;
  feedbackList: FeedbackListResponse | null;
  loading: boolean;
  error: string | null;
  refreshMetrics: () => Promise<void>;
  refreshFeedbackList: (options?: {
    step?: string;
    rating?: string;
  }) => Promise<void>;
}

export function useFeedbackAnalytics(): UseFeedbackAnalyticsResult {
  const [metrics, setMetrics] = useState<FeedbackMetrics | null>(null);
  const [feedbackList, setFeedbackList] = useState<FeedbackListResponse | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async (refresh = false) => {
    try {
      const data = await feedbackService.getFeedbackMetrics(refresh);
      setMetrics(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
    }
  }, []);

  const fetchFeedbackList = useCallback(
    async (options: { step?: string; rating?: string } = {}) => {
      try {
        const data = await feedbackService.listFeedback({
          limit: 50,
          ...options,
        });
        setFeedbackList(data);
        setError(null);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch feedback list'
        );
      }
    },
    []
  );

  const refreshMetrics = useCallback(async () => {
    setLoading(true);
    await fetchMetrics(true);
    setLoading(false);
  }, [fetchMetrics]);

  const refreshFeedbackList = useCallback(
    async (options: { step?: string; rating?: string } = {}) => {
      setLoading(true);
      await fetchFeedbackList(options);
      setLoading(false);
    },
    [fetchFeedbackList]
  );

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchMetrics(), fetchFeedbackList()]);
      setLoading(false);
    };
    loadData();
  }, [fetchMetrics, fetchFeedbackList]);

  return {
    metrics,
    feedbackList,
    loading,
    error,
    refreshMetrics,
    refreshFeedbackList,
  };
}
